<template>
  <v-card
    class="pt-6 pb-4"
    :class="{
      'report-card-faded': !isCompatible && !isSelected,
      'report-card-disabled': !canSelect && !isSelected,
    }"
  >
    <!-- Compare checkbox temporarily hidden -->
    <div class="compare-checkbox" v-if="false">
      <div class="d-flex flex-row justify-end align-center">
        <label
          :for="checkboxId"
          class="d-flex flex-row align-center"
          style="cursor: pointer"
          :class="{ 'disabled-label': !canSelect && !isSelected }"
        >
          <div class="text-caption pr-2">COMPARE</div>
          <v-checkbox
            :id="checkboxId"
            :model-value="isSelectedLocal"
            hide-details
            density="compact"
            color="primary"
            class="bordered-checkbox no-margin"
            :disabled="!canSelect && !isSelected"
            @update:model-value="handleSelection"
          ></v-checkbox>
        </label>
      </div>
    </div>

    <!-- Header with Image and Compare button -->
    <div class="content-container d-flex flex-column">
      <div class="flex-grow-1">
        <div class="d-flex flex-row align-start p-2">
          <div class="d-flex flex-column">
            <a
              :href="`/reports/overview/${report.slug}`"
              rel="noopener noreferrer"
              class="text-decoration-none"
            >
              <div
                class="report-image-container mr-4"
                :style="coverImageStyle"
                ref="coverImageContainer"
                role="img"
                :aria-label="
                  report.title ? `Cover for ${report.title}` : 'Report Cover'
                "
              />
            </a>
          </div>
          <div
            class="report-header-content d-flex flex-column justify-space-between mb-2"
          >
            <a
              :href="`/reports/overview/${report.slug}`"
              class="font-weight-bold text-secondary report-title text-decoration-none"
              rel="noopener noreferrer"
            >
              {{ report.title }}
            </a>
            <div class="metric-wrapper d-flex flex-row justify-start">
              <div class="d-flex flex-column mr-3">
                <img
                  :src="
                    getFormatIconPath(
                      (report.formats && report.formats[0]) || undefined,
                    )
                  "
                  alt=""
                  aria-hidden="true"
                  width="27"
                  height="27"
                />
              </div>
              <div class="d-flex flex-column">
                <div class="text-caption">FORMAT</div>
                <div class="text-body-2 text-secondary">
                  {{
                    report.formats && report.formats.length > 0
                      ? formatDisplayFormat(report.formats[0])
                      : 'N/A'
                  }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Info Grid -->
        <v-container class="pt-4 pb-3 px-2">
          <v-row no-gutters align-content="space-between">
            <v-col cols="3" :class="basicColumnClasses" sm="6" lg="6" xl="3">
              <div class="text-caption">SUBJECT</div>
              <div
                class="text-body-2 text-secondary font-weight-bold"
                @click.stop="glossaryStore.openGlossary('ela')"
              >
                {{ reportsStore.formatSubjectName(report.subject) || 'N/A' }}
              </div>
            </v-col>
            <v-col cols="3" :class="gradesColumnClasses" sm="6" lg="6" xl="3">
              <div class="text-caption">GRADES</div>
              <div class="text-body-2 text-secondary font-weight-bold">
                {{ formatGradeDisplay(report.grades || []) }}
              </div>
            </v-col>
            <v-col
              cols="3"
              :class="basicColumnClasses"
              class="me-auto"
              sm="6"
              lg="6"
              xl="3"
            >
              <div class="text-caption">RELEASED</div>
              <div class="text-body-2 text-secondary font-weight-bold">
                {{
                  report.release
                    ? new Date(report.release).toLocaleDateString()
                    : 'N/A'
                }}
              </div>
            </v-col>
            <v-col cols="3" :class="columnClasses" sm="6" lg="6" xl="3">
              <div class="text-caption">REVIEW TOOL</div>
              <div class="text-body-2 text-secondary font-weight-bold">
                {{
                  report.versions && report.versions.length > 0
                    ? report.versions[0]
                    : 'N/A'
                }}
              </div>
            </v-col>
          </v-row>
        </v-container>

        <!-- Gateway Status -->
        <v-container class="pa-0">
          <v-row no-gutters class="gateway-status mb-2">
            <v-col cols="6">
              <div
                v-for="(gateway, index) in report.gateways"
                :key="gateway.slug"
                :class="index === report.gateways.length - 1 ? 'mb-0' : 'mb-5'"
              >
                <div class="text-caption mb-1">
                  {{ gateway.label || 'N/A' }}
                </div>
                <div class="d-flex flex-row align-center">
                  <StatusIcon
                    :status="
                      mapGatewayStatusToIconStatus(gateway.review_status?.slug)
                    "
                  />
                  <div class="text-body-2">
                    {{ formatGatewayStatus(gateway.review_status?.name) }}
                  </div>
                </div>
              </div>
            </v-col>
            <v-col cols="6" class="pl-2">
              <div
                v-for="detail in report.details"
                :key="detail.name"
                class="mb-0"
              >
                <div
                  class="text-caption mb-1"
                  :class="{
                    clickable:
                      detail.name?.replace(/<[^>]*>/g, '') ===
                      'Multilingual Learner Supports',
                  }"
                  @click="
                    detail.name?.replace(/<[^>]*>/g, '') ===
                      'Multilingual Learner Supports' &&
                    glossaryStore.openGlossary('multilingual learner supports')
                  "
                  style="cursor: pointer"
                  v-if="
                    detail.name?.replace(/<[^>]*>/g, '') ===
                    'Multilingual Learner Supports'
                  "
                  role="button"
                  tabindex="0"
                  @keydown.enter.space.prevent="
                    detail.name?.replace(/<[^>]*>/g, '') ===
                      'Multilingual Learner Supports' &&
                    glossaryStore.openGlossary('multilingual learner supports')
                  "
                >
                  {{ detail.name?.replace(/<[^>]*>/g, '') || '' }}
                </div>
                <div class="text-caption mb-1" v-else>
                  {{ detail.name?.replace(/<[^>]*>/g, '') || '' }}
                </div>
                <div class="d-flex flex-row align-center">
                  <div class="text-body-2 text-secondary mb-4">
                    {{
                      detail.name?.replace(/<[^>]*>/g, '') ===
                        'Science of Reading' &&
                      detail.value?.toLowerCase() === 'available'
                        ? 'Scores Available'
                        : detail.value || 'N/A'
                    }}
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
        <div class="card-footer-group d-flex flex-column">
          <v-alert
            v-if="report.alerts?.length"
            :text="report.alerts[0]?.content?.replace(/<[^>]*>/g, '') || ''"
            type="warning"
            variant="tonal"
            class="text-uppercase"
            :icon="false"
            density="compact"
            style="font-size: 10px; font-weight: bold"
          ></v-alert>
          <div class="px-2">
            <div
              class="copyright text-secondary pt-3 pb-5"
              v-html="formattedCopyright"
            ></div>
          </div>
        </div>
      </div>
    </div>
    <v-container class="pa-0 px-4">
      <v-row>
        <v-col>
          <router-link
            :to="{ name: 'report-overview', params: { id: report.slug } }"
            class="btn btn-outline-secondary btn-block mt-auto mb-1"
          >
            <span class="text-secondary">View Full Report</span>
            <span class="btn-append">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                class="icon-arrow-right"
                width="16"
                height="16"
                fill="currentColor"
              >
                <path
                  d="M4 11v2h12l-5.5 5.5 1.42 1.42L19.84 12l-7.92-7.92L10.5 5.5 16 11z"
                ></path>
              </svg>
            </span>
          </router-link>
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>

<script setup lang="ts">
  import type { Report } from '../../store';
  import {
    useGlossaryStore,
    useCompareStore,
    useReportsStore,
  } from '../../store';
  import { useRouter } from 'vue-router';
  import { formatGradeDisplay } from '../../lib/grades';
  import { formatDisplayFormat } from '../../lib/formatUtils';
  import {
    computed,
    ref,
    onMounted,
    watch,
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
  } from 'vue';
  import { useDisplay } from 'vuetify';
  import StatusIcon from '../common/StatusIcon.vue';

  const props = defineProps<{
    report: Report;
    instanceId: string;
  }>();

  const glossaryStore = useGlossaryStore();
  const compareStore = useCompareStore();
  const reportsStore = useReportsStore();
  const router = useRouter();
  const { lgAndUp, xl, smAndUp } = useDisplay();
  const coverImageLoaded = ref(true);
  const fallbackCoverUrl = ref('');
  const instance = getCurrentInstance();
  const isPriority = ref(false);

  const columnClasses = computed(() => {
    return smAndUp.value ? 'd-flex flex-column mb-2' : 'd-flex flex-column';
  });

  const basicColumnClasses = computed(() => {
    return smAndUp.value ? 'd-flex flex-column mb-2' : 'd-flex flex-column';
  });

  const gradesColumnClasses = computed(() => {
    if (xl.value) {
      return 'd-flex flex-column mb-2';
    } else if (smAndUp.value) {
      return 'd-flex flex-column mb-2';
    } else {
      return 'd-flex flex-column';
    }
  });

  function debounce<T extends (...args: any[]) => void>(
    func: T,
    delay: number,
  ): (...args: Parameters<T>) => void {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    return (...args: Parameters<T>) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  }

  const isSelectedLocal = ref(
    props.instanceId
      ? compareStore.selectedReports.some(
          (item) => item.id === props.instanceId,
        )
      : false,
  );

  const checkboxId = `compare-checkbox-${props.instanceId}`;

  const isCompatible = computed(() =>
    compareStore.isReportCompatible(props.report),
  );
  const isSelected = computed(() =>
    compareStore.isReportSelected(props.instanceId),
  );
  const canSelect = computed(() => {
    return (
      isSelected.value || (isCompatible.value && compareStore.canAddMoreReports)
    );
  });

  const reportUrl = computed(() => {
    return router.resolve({
      name: 'report-overview',
      params: { id: props.report.slug },
    }).href;
  });

  const formatGatewayStatus = (statusName: string | undefined): string => {
    if (statusName === 'Meets') return 'Meets Expectations';
    if (!statusName || statusName === 'Does Not Exist') return 'Not Eligible';
    return statusName;
  };

  // Computed property to format copyright by removing the dash
  const formattedCopyright = computed(() => {
    if (!props.report.copyright) return '';

    let result = props.report.copyright;

    // More aggressive approach - remove any dash-like character between year and publisher
    // This pattern matches: year(s) + any whitespace + any dash character + any whitespace + non-digit
    result = result.replace(/(\d{4}(?:-\d{4})?)\s*[–—\-]\s*(?=\D)/g, '$1 ');

    // Also handle HTML entities for dashes
    result = result.replace(/(\d{4}(?:-\d{4})?)\s*&#8211;\s*/g, '$1 ');
    result = result.replace(/(\d{4}(?:-\d{4})?)\s*&#8212;\s*/g, '$1 ');
    result = result.replace(/(\d{4}(?:-\d{4})?)\s*&ndash;\s*/g, '$1 ');
    result = result.replace(/(\d{4}(?:-\d{4})?)\s*&mdash;\s*/g, '$1 ');

    // Clean up multiple spaces and trim
    return result.replace(/\s+/g, ' ').trim();
  });

  function mapGatewayStatusToIconStatus(
    statusSlug: string | undefined,
  ): string {
    if (!statusSlug) return 'gray';
    const statusMap: Record<string, string> = {
      meets: 'green',
      'partially-meets': 'yellow',
      'does-not-meet': 'red',
      'not-eligible': 'gray',
      'does-not-exist': 'gray',
      'no-status': 'gray',
      'narrative-only': 'gray',
      'zero-score': 'red',
    };
    return statusMap[statusSlug] || 'gray';
  }

  const placeholderImage =
    'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAzMDAgNDAwIiB3aWR0aD0iNzIiIGhlaWdodD0iOTUiPjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjZWJmN2ZiIi8+PC9zdmc+';

  const coverImageStyle = computed(() => {
    if (!coverImageLoaded.value && fallbackCoverUrl.value) {
      return {
        backgroundImage: `url(${fallbackCoverUrl.value})`,
      };
    }

    return {
      backgroundImage: props.report.cover
        ? `url(${props.report.cover})`
        : `url(${placeholderImage})`,
      backgroundSize: 'cover',
    };
  });

  const formatIconCache = new Map();

  function getFormatIconPath(formatType: string | undefined): string {
    if (!formatType) {
      return (
        formatIconCache.get('default') ||
        (formatIconCache.set(
          'default',
          new URL('@/assets/icon-format-3.svg', import.meta.url).href,
        ) &&
          formatIconCache.get('default'))
      );
    }

    // Normalize the format type
    const normalized = formatType
      .toLowerCase()
      .replace(/[:\s-]+/g, ' ')
      .trim();
    const key = normalized;

    if (formatIconCache.has(key)) {
      return formatIconCache.get(key);
    }

    // Debug log to see exact format type
    console.log('Original format type:', formatType);
    console.log('Normalized key:', key);

    let iconUrl;
    switch (key) {
      // Core Comprehensive formats (format-3)
      case 'core comprehensive':
      case 'core course':
      case 'core':
        iconUrl = new URL('@/assets/icon-format-3.svg', import.meta.url).href;
        break;

      // Core without foundational skills (format-2)
      case 'core no foundational skills':
      case 'support':
        iconUrl = new URL('@/assets/icon-format-2.svg', import.meta.url).href;
        break;

      // Supplemental/foundational skills (format-1)
      case 'supplemental foundational skills':
      case 'supplemental foundational skills only':
      case 'supplement':
      case 'supplemental':
        iconUrl = new URL('@/assets/icon-format-1.svg', import.meta.url).href;
        break;

      // Default to format-3 for unknown types
      default:
        console.log('Unknown format type:', formatType, 'Normalized:', key);
        iconUrl = new URL('@/assets/icon-format-3.svg', import.meta.url).href;
    }

    formatIconCache.set(key, iconUrl);
    return iconUrl;
  }

  function handleSelection(value: boolean | null) {
    const selected = !!value;

    if (selected) {
      const success = compareStore.addReport(props.instanceId, props.report);
      isSelectedLocal.value = success;
    } else {
      compareStore.removeReport(props.instanceId);
      isSelectedLocal.value = false;
    }
  }

  const viewFullReport = () => {
    if (
      router.currentRoute.value.name === 'report-overview' &&
      router.currentRoute.value.params.id === props.report.slug
    ) {
      return;
    }
    router.push({
      name: 'report-overview',
      params: { id: props.report.slug },
    });
  };

  function validateCoverImage() {
    if (!props.report.cover) {
      coverImageLoaded.value = false;
      fallbackCoverUrl.value = placeholderImage;
      return;
    }

    const img = new Image();

    if ('fetchPriority' in img) {
      (img as any).fetchPriority = isPriority.value ? 'high' : 'low';
    }

    img.onload = () => {
      coverImageLoaded.value = true;
    };

    img.onerror = () => {
      coverImageLoaded.value = false;
      fallbackCoverUrl.value = placeholderImage;
    };

    img.src = props.report.cover || '';
  }

  function optimizeLCPForPriorityCards(cardElement: HTMLElement) {
    if (cardElement.closest('.priority')) {
      isPriority.value = true;

      const reportImage = cardElement.querySelector('.report-image-container');
      if (reportImage) {
        reportImage.setAttribute('data-lcp-priority', 'true');
      }
    }
  }

  function setGatewayStatusHeights() {
    nextTick(() => {
      const all = document.querySelectorAll('.gateway-status');
      if (!all.length) return;

      all.forEach((el) => {
        (el as HTMLElement).style.height = 'auto';
      });

      let maxHeight = 0;
      all.forEach((el) => {
        const h = (el as HTMLElement).offsetHeight;
        if (h > maxHeight) maxHeight = h;
      });

      all.forEach((el) => {
        (el as HTMLElement).style.height = maxHeight + 'px';
      });
    });
  }

  const debouncedSetGatewayStatusHeights = debounce(
    setGatewayStatusHeights,
    150,
  );

  onMounted(() => {
    if (instance && instance.vnode.el) {
      optimizeLCPForPriorityCards(instance.vnode.el as HTMLElement);
    }
    validateCoverImage();
    if (instance && instance.vnode.el) {
      const el = instance.vnode.el as HTMLElement;
      const touchTargets = el.querySelectorAll(
        'button, a, .v-checkbox, [role="button"]',
      );
      touchTargets.forEach((target) => {
        target.addEventListener('touchstart', () => {}, { passive: true });
      });
    }
    debouncedSetGatewayStatusHeights();
    window.addEventListener('resize', debouncedSetGatewayStatusHeights);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', debouncedSetGatewayStatusHeights);
  });

  watch(
    [() => props.report.gateways, () => props.report.details],
    () => {
      debouncedSetGatewayStatusHeights();
    },
    { deep: true, immediate: false },
  );

  watch(
    () => compareStore.selectedReports,
    () => {
      if (isSelectedLocal.value !== isSelected.value) {
        requestAnimationFrame(() => {
          isSelectedLocal.value = isSelected.value;
        });
      }
    },
    { deep: true },
  );
</script>

<style lang="scss" scoped>
  $image-width: 72px;
  $image-height: 95px;
  $font-size-small: 10px;
  $font-size-base: 12px;
  $font-size-large: 16px;
  $spacing-base: 12px;
  $spacing-large: 18px;
  $border-radius: 8px;

  @mixin flex-center {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @mixin text-base {
    font-size: $font-size-base;
    color: rgb(var(--v-theme-text-secondary));
  }

  :deep(.report-card),
  .report-card {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .content-container {
    position: relative;
    padding: $spacing-base $spacing-base 0;
    contain: content;
    content-visibility: auto;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .compare-checkbox {
    position: absolute;
    top: 0;
    right: 14px;
    touch-action: manipulation;
  }

  .d-flex.flex-row.align-start {
    align-items: flex-start;
    align-items: stretch;
  }

  .report-header-content {
    min-height: 95px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .report-image-container {
    display: flex;
    width: $image-width;
    height: $image-height;
    border: solid 1px rgb(var(--v-theme-light-grey));
    background-size: cover;
    background-position: center;
    will-change: transform;
    transform: translateZ(0);
    backface-visibility: hidden;
    contain: paint;
  }

  :deep(.v-application .v-card a.report-title:not(.v-btn):not(.v-tab)) {
    font-family: 'rockwell', sans-serif !important;
    font-size: $font-size-large !important;
    font-weight: normal !important;
    line-height: 1.2;
    cursor: pointer;
    color: rgb(var(--v-theme-text-secondary)) !important;
  }

  .metric-wrapper {
    .text-caption {
      text-transform: uppercase !important;
    }
    .text-body-2 {
      font-size: 11px !important;
    }
    &.d-flex.flex-row {
      align-items: center;
    }
    img {
      margin: 0;
      display: block;
    }
  }

  .gateway-status {
    background-color: rgba(var(--v-theme-blue), 0.05);
    border-radius: $border-radius;
    padding: $spacing-large $spacing-base;
    contain: content;

    .text-body-2 {
      display: inline-block;
      font-size: 11px !important;
    }

    .text-caption {
      text-transform: uppercase !important;
    }
  }

  .copyright {
    font-size: $font-size-base !important;
    font-family: 'proxima-nova', sans-serif !important;
    line-height: 1.2;
    padding: 2px 0 8px 0;
    min-height: 32px;

    :deep(*) {
      font-size: $font-size-base !important;
      font-family: 'proxima-nova', sans-serif !important;
      line-height: 1.2;
      margin: 0;
      padding: 0;
    }
  }

  .v-btn,
  a.btn {
    border-width: 2px !important;
    border-radius: 6px !important;
    transition: border-color 0.2s ease-in-out;
    touch-action: manipulation;

    // Anchor-specific v-btn styling
    &.btn {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      min-height: 40px !important;
      padding: 8px 16px !important;
      font-size: 14px !important;
      line-height: 1.25 !important;
      text-align: center !important;
      text-transform: none !important;
      letter-spacing: normal !important;
      border: 2px solid #d4d4d4 !important;
      text-decoration: none !important;
    }

    :deep(.v-btn__overlay) {
      display: none;
    }

    :deep(.v-btn__content),
    .text-secondary {
      color: rgb(var(--v-theme-secondary)) !important;
      font-weight: bold;
      transition: color 0.2s ease-in-out;
      font-size: 14px !important;
    }

    :deep(.v-btn__append),
    .btn-append {
      margin-left: 8px;
      display: flex;
      align-items: center;

      .v-icon,
      svg {
        color: rgb(var(--v-theme-secondary)) !important;
        fill: rgb(var(--v-theme-secondary)) !important;
        transition: color 0.2s ease-in-out;
        width: 20px;
        height: 20px;
      }
    }

    &:hover {
      border-color: rgb(var(--v-theme-blue)) !important;
      background-color: rgba(var(--v-theme-blue), 0.05) !important;
      text-decoration: none !important;

      :deep(.v-btn__content),
      .text-secondary {
        color: rgb(var(--v-theme-primary)) !important;
      }

      :deep(.v-btn__append),
      .btn-append {
        .v-icon,
        svg {
          color: rgb(var(--v-theme-secondary)) !important;
          fill: rgb(var(--v-theme-secondary)) !important;
        }
      }
    }

    &:focus {
      text-decoration: none !important;
    }
  }

  .v-alert.text-warning {
    :deep(.v-alert__content) {
      font-size: $font-size-small;
      font-weight: bold;
      color: #02080c !important;
    }
  }

  .reports-grid {
    width: 100%;
    max-width: 100vw;
    min-height: 500px;
    contain: layout;
    overflow-x: auto;
  }

  .reports-row {
    display: grid;
    gap: 12px;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    align-items: stretch;
    contain: layout;
  }

  .card-footer-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .report-card-faded {
    opacity: 0.5;
    transition: opacity 0.3s ease;
  }

  .report-card-disabled {
    pointer-events: none;
    opacity: 0.3;
  }

  .disabled-label {
    opacity: 0.6;
    cursor: not-allowed !important;
  }

  .v-checkbox.v-input--disabled {
    opacity: 0.6;
  }
</style>
